import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function getUserFromStorage() {
  const raw = localStorage.getItem("user");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export const authApi = {
  login: async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("token", data.token);
    if (data?.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
    if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
    return data;
  },
  logout: async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    try {
      if (refreshToken) await api.post("/auth/logout", { refreshToken });
    } catch (_) {
      // Ignore logout API failures and still clear local state.
    }
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  },
  getCurrentUser: getUserFromStorage,
};

export const adminApi = {
  getDashboard: async () => (await api.get("/admin/dashboard")).data,
  getUsers: async (params) => (await api.get("/admin/users", { params })).data,
  getUserDetails: async (userId, params) =>
    (await api.get(`/admin/users/${userId}/details`, { params })).data,
  updateUser: async (userId, payload) =>
    (await api.patch(`/admin/users/${userId}`, payload)).data,
  updateUserStatus: async (userId, isActive) =>
    (await api.patch(`/admin/users/${userId}/status`, { isActive })).data,
  updateUserRole: async (userId, role) =>
    (await api.patch(`/admin/users/${userId}/role`, { role })).data,
  resetUser2FA: async (userId) => (await api.post(`/admin/users/${userId}/reset-2fa`)).data,
  creditWallet: async (userId, amount, reason) =>
    (await api.post(`/admin/wallet/${userId}/credit`, { amount, reason })).data,
  debitWallet: async (userId, amount, reason) =>
    (await api.post(`/admin/wallet/${userId}/debit`, { amount, reason })).data,
  getFinanceFeed: async (params) => (await api.get("/admin/finance/feed", { params })).data,
  getTrades: async (params) => (await api.get("/admin/trades", { params })).data,
  closeTrade: async (tradeId) => (await api.post(`/admin/trades/${tradeId}/close`)).data,
  getStakes: async (params) => (await api.get("/admin/stakes", { params })).data,
  getTeamManagement: async (params) =>
    (await api.get("/admin/team/management", { params })).data,
  getDeposits: async (params) => (await api.get("/admin/deposits", { params })).data,
  getLicenses: async (params) => (await api.get("/admin/licenses", { params })).data,
  assignLicense: async (userId) => (await api.post(`/admin/licenses/${userId}/assign`)).data,
  revokeLicense: async (userId) => (await api.post(`/admin/licenses/${userId}/revoke`)).data,
  getMlmBonusOverview: async () => (await api.get("/admin/mlm/bonus/overview")).data,
  getMlmBonusEvents: async (params) => (await api.get("/admin/mlm/bonus/events", { params })).data,
  getLogs: async (params) => (await api.get("/admin/logs", { params })).data,
  getOperationsSummary: async () => (await api.get("/admin/operations/summary")).data,
  getSettings: async () => (await api.get("/admin/settings")).data,
  updateSettings: async (payload) => (await api.patch("/admin/settings", payload)).data,
  getCompensationConfig: async () => (await api.get("/admin/compensation/config")).data,
  updateCompensationConfig: async (payload) =>
    (await api.patch("/admin/compensation/config", payload)).data,
  getCompensationLedger: async (params) =>
    (await api.get("/admin/compensation/ledger", { params })).data,
  getCompensationSummary: async () => (await api.get("/admin/compensation/summary")).data,
  getCommissions: async (params) => (await api.get("/admin/commissions", { params })).data,
  getWithdrawals: async (params) => (await api.get("/admin/withdrawals/all", { params })).data,
  getWithdrawalStats: async () => (await api.get("/admin/withdrawals/statistics")).data,
  approveWithdrawal: async (id) => (await api.post(`/admin/withdrawals/${id}/approve`)).data,
  rejectWithdrawal: async (id, reason) =>
    (await api.post(`/admin/withdrawals/${id}/reject`, { reason })).data,
  retryWithdrawal: async (id) => (await api.post(`/admin/withdrawals/${id}/retry`)).data,
};
